import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
	deleteLocalStorageBalance,
	setLocalStorageBalance,
} from 'utils/localStorageHelpers/balanceHelpers';

import {
	ChatGPTModels,
	ChatGPTStreamingTemplateNames,
	ChatGPTSubStageTemplateNames,
	ChatGPTTemplateNames,
	PaymentStatus,
} from 'core/enums/common';
import { UserFile } from 'core/types/base';

import {
	addNewUser,
	changeUserPassword,
	deleteUserAccount,
	embedDocument,
	fetchUserBalance,
	forgotPasswordRequest,
	getAllPaymentMethods,
	getCustomerPayments,
	getUserById,
	getUserDocuments,
	loginUser,
	resetPassword,
	updateUserProfile,
} from './asyncActions';
import { IUserReducer } from './types';

const initialAiRequestsModelsConfiguration = {
	[ChatGPTSubStageTemplateNames['FeaturesConversationTemplate']]: ChatGPTModels['GPT4'],
	[ChatGPTStreamingTemplateNames['PotentialSummaryTemplate']]: ChatGPTModels['GPT4'],
	[ChatGPTTemplateNames['UserStoriesEpicsTemplate']]: ChatGPTModels['GPT4o'],
	[ChatGPTTemplateNames['UseCaseDiagramTemplate']]: ChatGPTModels['GPT4-turbo'],
	[ChatGPTTemplateNames['TestPlanTemplate']]: ChatGPTModels['GPT4o'],
};

const initialState: IUserReducer = {
	ticketSaveTrigger: false,
	isOpenUserProfile: false,
	isOpenNoDeleteAccess: false,
	isExportModalOpen: false,
	stripeConnection: false,
	userFiles: [],
	loadedUsers: [],
	userFilesPagination: null,
	currentSubscription: null,
	aiRequestsModelsConfiguration: initialAiRequestsModelsConfiguration,
	cancelToken: new AbortController(),
};

const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		removeSignUpError: (state) => {
			state.signUpError = null;
		},
		removeLoginError: (state) => {
			state.loginError = null;
		},
		setPaymentSuccess: (state) => {
			state.paymentStatus = PaymentStatus.success;
		},
		setPaymentCancel: (state) => {
			state.paymentStatus = PaymentStatus.cancel;
		},
		setPaymentMethod: (state, { payload }) => {
			state.paymentMethods = payload;
		},
		setMethodSuccess: (state) => {
			state.methodStatus = PaymentStatus.success;
		},
		setMethodCancel: (state) => {
			state.methodStatus = PaymentStatus.cancel;
		},
		clearMethodStatus: (state) => {
			state.methodStatus = null;
		},
		setIsOpenUserProfile: (state, { payload }) => {
			state.isOpenUserProfile = payload;
		},
		setCurrentSubscription: (state, { payload }) => {
			state.currentSubscription = payload;
		},
		clearPaymentStatus: (state) => {
			state.paymentStatus = null;
		},
		setNotEnoughTokensError: (state, action) => {
			state.notEnoughTokensError = action.payload;
		},
		triggerTicketSave: (state) => {
			state.ticketSaveTrigger = true;
		},
		resetTicketSave: (state) => {
			state.ticketSaveTrigger = false;
		},
		setDocumentProcessing: (state, { payload }) => {
			state.userFiles = state?.userFiles?.map((file) =>
				file.name === payload.name ? { ...file, isReadyToUse: payload.status } : file
			);
		},
		chooseFile: (state, { payload }) => {
			state.choosedFile = payload;
		},
		setAiRequestsModelsConfiguration: (state, { payload }) => {
			state.aiRequestsModelsConfiguration = payload;
		},
		setStripeConnection: (state, { payload }) => {
			state.stripeConnection = payload;
		},
		setStripeInstance: (state, { payload }) => {
			state.stripeInstance = payload;
		},
		setDeniedAccessErrorOpen: (state, { payload }) => {
			state.deniedAccessError = payload;
		},
		setQueueDelayNotificationOpen: (state, { payload }) => {
			state.queueDelayNotification = payload;
		},
		addLoadedUser: (state, { payload }) => {
			if (!state.loadedUsers.find((user) => user.id === payload.id))
				state.loadedUsers.push(payload);
		},
		setIsExportModalOpen: (state, { payload }) => {
			state.isExportModalOpen = payload;
		},
		setIsOpenNoDeleteAccess: (state, { payload }) => {
			state.isOpenNoDeleteAccess = payload;
		},
		logout: (state) => {
			state.cancelToken?.abort();
			return { ...initialState, cancelToken: new AbortController() };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(addNewUser.pending, (state) => {
				state.loading = true;
			})
			.addCase(addNewUser.fulfilled, (state) => {
				state.loading = false;
				state.signUpError = null;
			})
			.addCase(addNewUser.rejected, (state, action: PayloadAction<any>) => {
				state.loading = false;
				state.signUpError = action.payload;
			})
			.addCase(loginUser.pending, (state) => {
				state.loading = true;
			})
			.addCase(loginUser.fulfilled, (state, action) => {
				state.loading = false;
				state.loginError = null;
				Object.assign(state, action.payload);
			})
			.addCase(loginUser.rejected, (state, action: PayloadAction<any>) => {
				state.loading = false;
				state.loginError = action.payload;
			})
			.addCase(forgotPasswordRequest.pending, (state) => {
				state.loading = true;
			})
			.addCase(forgotPasswordRequest.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(forgotPasswordRequest.rejected, (state) => {
				state.loading = false;
			})
			.addCase(resetPassword.pending, (state) => {
				state.loading = true;
			})
			.addCase(resetPassword.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(resetPassword.rejected, (state) => {
				state.loading = false;
			})
			.addCase(fetchUserBalance.fulfilled, (state, action) => {
				state.getUserBalanceError = null;
				state.userBalance = action.payload;

				setLocalStorageBalance(action.payload);
			})
			.addCase(fetchUserBalance.rejected, (state, action: PayloadAction<any>) => {
				state.getUserBalanceError = action.payload;

				deleteLocalStorageBalance();
			})
			.addCase(changeUserPassword.pending, (state) => {
				state.loading = true;
			})
			.addCase(changeUserPassword.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(deleteUserAccount.pending, (state) => {
				state.loading = true;
			})
			.addCase(deleteUserAccount.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(updateUserProfile.fulfilled, (state) => {
				state.signUpError = null;
			})
			.addCase(updateUserProfile.rejected, (state, action: PayloadAction<any>) => {
				state.signUpError = action.payload;
			})
			.addCase(getUserDocuments.fulfilled, (state, { payload }) => {
				const { data, ...pagination } = payload;
				state.userFiles = data;
				state.userFilesPagination = pagination;
				state.loading = false;
			})
			.addCase(getUserDocuments.pending, (state) => {
				state.loading = true;
			})
			.addCase(getUserDocuments.rejected, (state) => {
				state.loading = false;
			})
			.addCase(getAllPaymentMethods.fulfilled, (state, { payload }) => {
				state.paymentMethods = payload.data.data;
				state.hasMorePayments = {
					end: payload.isStart || (payload.isEnd && payload.data.hasMore),
					start: (payload.isStart && payload.data.hasMore) || payload.isEnd || payload.data.hasMore,
				};
			})
			.addCase(getCustomerPayments.fulfilled, (state, { payload }) => {
				state.billingHistory = payload.data.data;
				state.hasMoreHistory = {
					end: payload.isStart || (payload.isEnd && payload.data.hasMore),
					start: (payload.isStart && payload.data.hasMore) || payload.isEnd || payload.data.hasMore,
				};
			})
			.addCase(embedDocument.fulfilled, (state, { payload }) => {
				state.userFiles = state.userFiles
					? state.userFiles.map((file: UserFile) =>
							file.name === payload.name ? payload.data : file
					  )
					: [];
			})
			.addCase(getUserById.fulfilled, (state, { payload }) => {
				const foundUser = state.loadedUsers.find((user) => user.id === payload.id);

				if (!foundUser) state.loadedUsers.push(payload);
			});
	},
});

export const {
	removeSignUpError,
	removeLoginError,
	setPaymentCancel,
	setPaymentSuccess,
	clearPaymentStatus,
	setNotEnoughTokensError,
	setCurrentSubscription,
	triggerTicketSave,
	resetTicketSave,
	setIsOpenUserProfile,
	setIsExportModalOpen,
	setDocumentProcessing,
	chooseFile,
	setAiRequestsModelsConfiguration,
	setMethodSuccess,
	setMethodCancel,
	clearMethodStatus,
	setStripeConnection,
	setStripeInstance,
	setPaymentMethod,
	addLoadedUser,
	setDeniedAccessErrorOpen,
	setQueueDelayNotificationOpen,
	setIsOpenNoDeleteAccess,
	logout,
} = userSlice.actions;

export default userSlice.reducer;
